import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Row, Col, Card, CardBody, CardTitle, CardHeader, Input, Modal, ModalHeader, ModalBody, Button
} from "reactstrap";
import {
  Table, Thead, Tbody, Tr, Th, Td
} from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import CustomPagination from "components/Common/CustomPagination";
import TableLoader from "components/Common/TableLoader";
import DeleteModal from "components/Common/DeleteModal";
import { ReminderStatusOptions } from "components/Common/ReminderStatusOptions";
import formatDate from "helpers/formatDate";
import { scheduleDateToLocalInput } from "helpers/formatScheduleDate";
import {
  fetchRemindersStart,
  addReminderStart,
  editReminderStart,
  deleteReminderStart,
} from "store/reminders/actions";
import { fetchDictionaryStart } from "store/dictionary/actions";

function RemindersTab(props) {
  const dispatch = useDispatch();
  const { clientId, currentUserId } = props;

  const [sizePerPage, setSizePerPage] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  // Form states
  const [note, setNote] = useState("");
  const [nextCallDate, setNextCallDate] = useState("");
  const [callStatus, setCallStatus] = useState("open");

  const {
    reminders = [],
    loading,
    totalDocs = 0,
    totalPages = 1,
    page = 1,
    hasNextPage = false,
    hasPrevPage = false,
    nextPage = null,
    prevPage = null,
    addLoading,
    editLoading,
    deleteLoading,
    modalClearCounter,
    deleteClearCounter,
  } = useSelector((state) => state.remindersReducer || {});

  const { callStatus: csOptions } = useSelector((state) => state.dictionaryReducer || {});
  const quickActionClearCounter = useSelector((state) => state.todosReducer?.clearingCounter || 0);

  const callStatusOptions = React.useMemo(() => {
    if (!csOptions) return [];
    return Array.isArray(csOptions) ? csOptions : Object.keys(csOptions);
  }, [csOptions]);

  const loadData = (pageNumber = 1, pageLimit = sizePerPage) => {
    dispatch(
      fetchRemindersStart({
        page: pageNumber,
        limit: parseInt(pageLimit),
        customerId: clientId,
      })
    );
  };

  useEffect(() => {
    dispatch(fetchDictionaryStart());
  }, [dispatch]);

  useEffect(() => {
    loadData(1);
  }, [clientId, sizePerPage, modalClearCounter, deleteClearCounter, quickActionClearCounter]);

  const handleOpenAddModal = () => {
    setNote("");
    setNextCallDate("");
    setCallStatus("open");
    setShowAddModal(true);
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!note || !nextCallDate) return;

    dispatch(
      addReminderStart({
        customerId: clientId,
        note,
        nextCallDate: new Date(nextCallDate).toISOString(),
        callStatus,
      })
    );
    setShowAddModal(false);
  };

  const handleOpenEditModal = (item) => {
    setSelectedReminder(item);
    setNote(item.note || "");
    setNextCallDate(scheduleDateToLocalInput(item.nextCallDate));
    setCallStatus(item.callStatus || "open");
    setShowEditModal(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!selectedReminder || !note || !nextCallDate) return;

    dispatch(
      editReminderStart({
        id: selectedReminder._id,
        note,
        nextCallDate: new Date(nextCallDate).toISOString(),
        callStatus,
      })
    );
    setShowEditModal(false);
  };

  const handleDeleteSubmit = () => {
    if (!selectedReminder) return;
    dispatch(
      deleteReminderStart({
        id: selectedReminder._id,
      })
    );
    setShowDeleteModal(false);
  };

  const handleInlineStatusChange = (reminderId, newStatus) => {
    dispatch(
      editReminderStart({
        id: reminderId,
        callStatus: newStatus,
      })
    );
  };

  return (
    <React.Fragment>
      <Row className="mb-4">
        <Col className="col-12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle className="color-primary mb-0">{props.t("Reminder")}</CardTitle>
              <Button color="primary" size="sm" onClick={handleOpenAddModal}>
                + {props.t("Add New Reminder")}
              </Button>
            </CardHeader>
            <CardBody>
              <div className="table-rep-plugin">
                <div className="table-responsive mb-0" data-pattern="priority-columns">
                  <Table className="table table-hover align-middle text-center">
                    <Thead className="table-light">
                      <Tr>
                        <Th><span className="color-primary">{props.t("ID")}</span></Th>
                        <Th><span className="color-primary">{props.t("Note")}</span></Th>
                        <Th><span className="color-primary">{props.t("Posted By")}</span></Th>
                        <Th><span className="color-primary">{props.t("Date Created")}</span></Th>
                        <Th><span className="color-primary">{props.t("Next Call Date & Time")}</span></Th>
                        <Th><span className="color-primary">{props.t("Call Status")}</span></Th>
                        <Th><span className="color-primary">{props.t("Action")}</span></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {loading && <TableLoader colSpan={7} />}
                      {!loading && reminders.length === 0 && (
                        <Tr>
                          <Td colSpan={7} className="text-center">
                            {props.t("(No records)")}
                          </Td>
                        </Tr>
                      )}
                      {!loading &&
                        reminders.map((obj) => {
                          const postedBy = obj.createdBy
                            ? `${obj.createdBy.firstName || ""} ${obj.createdBy.lastName || ""}`.trim()
                            : "";
                          const canEdit =
                            currentUserId === obj.createdBy?._id || currentUserId === obj.createdBy;

                          return (
                            <Tr key={obj._id}>
                              <Td>{obj.recordId || "-"}</Td>
                              <Td style={{
                                maxWidth: "250px",
                                whiteSpace: "normal"
                              }}>
                                {obj.note}
                              </Td>
                              <Td>{postedBy || "-"}</Td>
                              <Td>{obj.createdAt ? formatDate(obj.createdAt) : "-"}</Td>
                              <Td>{obj.nextCallDate ? formatDate(obj.nextCallDate) : "-"}</Td>
                              <Td style={{ minWidth: "150px" }}>
                                <Input
                                  type="select"
                                  value={obj.callStatus || "open"}
                                  onChange={(e) => handleInlineStatusChange(obj._id, e.target.value)}
                                  className="form-select form-select-sm text-capitalize"
                                >
                                  <ReminderStatusOptions options={callStatusOptions} value={obj.callStatus} />
                                </Input>
                              </Td>
                              <Td>
                                <div className={!canEdit ? "d-none" : ""}>
                                  <Link
                                    className="p-2 text-success"
                                    to="#"
                                    onClick={() => handleOpenEditModal(obj)}
                                  >
                                    <i className="mdi mdi-pencil font-size-18"></i>
                                  </Link>
                                  <Link
                                    className="p-2 text-danger"
                                    to="#"
                                    onClick={() => {
                                      setSelectedReminder(obj);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <i className="mdi mdi-delete font-size-18"></i>
                                  </Link>
                                </div>
                              </Td>
                            </Tr>
                          );
                        })}
                    </Tbody>
                  </Table>
                </div>
              </div>
              <CustomPagination
                totalPages={totalPages}
                docs={reminders}
                sizePerPage={parseInt(sizePerPage)}
                page={page}
                totalDocs={totalDocs}
                hasNextPage={hasNextPage}
                hasPrevPage={hasPrevPage}
                nextPage={nextPage}
                prevPage={prevPage}
                setSizePerPage={(size) => {
                  setSizePerPage(parseInt(size));
                }}
                onChange={(pageNumber, size) => loadData(pageNumber, size || sizePerPage)}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add Modal */}
      <Modal isOpen={showAddModal} toggle={() => setShowAddModal(!showAddModal)} centered>
        <ModalHeader toggle={() => setShowAddModal(!showAddModal)}>
          {props.t("Add New Reminder")}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleAddSubmit}>
            <div className="mb-3">
              <label className="form-label">{props.t("Note")}</label>
              <textarea
                className="form-control"
                rows="3"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{props.t("Next Call Date & Time")}</label>
              <input
                type="datetime-local"
                className="form-control"
                value={nextCallDate}
                onChange={(e) => setNextCallDate(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{props.t("Call Status")}</label>
              <Input
                type="select"
                className="form-select text-capitalize"
                value={callStatus}
                onChange={(e) => setCallStatus(e.target.value)}
              >
                <ReminderStatusOptions options={callStatusOptions} value={callStatus} />
              </Input>
            </div>
            <div className="text-end">
              <Button color="secondary" className="me-2" onClick={() => setShowAddModal(false)}>
                {props.t("Cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={addLoading}>
                {addLoading ? props.t("Adding...") : props.t("Add Reminder")}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} toggle={() => setShowEditModal(!showEditModal)} centered>
        <ModalHeader toggle={() => setShowEditModal(!showEditModal)}>
          {props.t("Edit Reminder")}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleEditSubmit}>
            <div className="mb-3">
              <label className="form-label">{props.t("Note")}</label>
              <textarea
                className="form-control"
                rows="3"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{props.t("Next Call Date & Time")}</label>
              <input
                type="datetime-local"
                className="form-control"
                value={nextCallDate}
                onChange={(e) => setNextCallDate(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{props.t("Call Status")}</label>
              <Input
                type="select"
                className="form-select text-capitalize"
                value={callStatus}
                onChange={(e) => setCallStatus(e.target.value)}
              >
                <ReminderStatusOptions options={callStatusOptions} value={callStatus} />
              </Input>
            </div>
            <div className="text-end">
              <Button color="secondary" className="me-2" onClick={() => setShowEditModal(false)}>
                {props.t("Cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={editLoading}>
                {editLoading ? props.t("Saving...") : props.t("Save Changes")}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        loading={deleteLoading}
        onDeleteClick={handleDeleteSubmit}
        show={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
      />
    </React.Fragment>
  );
}

export default withTranslation()(RemindersTab);
